import { fmtDate } from "utils/date";
import React, { useState, useEffect, useRef } from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Table,
  Row,
  Col,
  Button,
  Input,
  FormGroup,
  Label,
  Spinner,
} from "reactstrap";
import { useNavigate } from "react-router-dom";
import NotificationAlert from "react-notification-alert";
import "react-notification-alert/dist/animate.css";
import { AlumnoService } from "service/AlumnoService";
import { CicloService } from "service/CicloService";
import { SalonService } from "service/SalonService";
import FSelect from "components/Select/FSelect";
import { CarnetService } from "service/CarnetService";
import CustomPagination from "components/Pagination/Pagination";

function Alumno() {
  const navigate = useNavigate();
  const notificationAlertRef = useRef(null);

  const notify = (type, message) => {
    notificationAlertRef.current.notificationAlert({
      place: "tc",
      message: <div>{message}</div>,
      type,
      icon: "tim-icons icon-bell-55",
      autoDismiss: 5,
    });
  };

  const [listAlumnos, setListAlumnos] = useState([]);
  const [listFiltered, setListFiltered] = useState([]);
  const [listCiclos, setListCiclos] = useState([]);
  const [listSalones, setListSalones] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [filters, setFilters] = useState({
    tipociclo: localStorage.getItem("_c") || "",
    salon: localStorage.getItem("_s") || "0",
  });

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  const [loadingBuscar, setLoadingBuscar] = useState(false);
  const [loadingCarnet, setLoadingCarnet] = useState(null);
  const [printedMap, setPrintedMap] = useState({});

  const handleEdit = (row) => {
    navigate(`/admin/alumno/${row.alumnoInfo._id}`);
  };

  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = listFiltered.slice(indexOfFirstItem, indexOfLastItem);

  const alumnoService = new AlumnoService();
  const cicloService = new CicloService();
  const salonService = new SalonService();
  const carnetService = new CarnetService();

  useEffect(() => {
    loadInitialData();
  }, []);

  // Sin auto-load: el usuario activa la búsqueda con el botón "Buscar"

  const loadInitialData = async () => {
    try {
      const resCiclos = await cicloService.getCiclos();
      const resSalones = await salonService.getSalones();
      
      const ciclos = resCiclos?.data || [];
      const salones = resSalones?.data || [];
      
      setListCiclos(ciclos);
      setListSalones(salones);

      if (!filters.tipociclo && ciclos.length > 0) {
        setFilters(prev => ({ ...prev, tipociclo: ciclos[0]._id }));
      }
    } catch (error) {
      console.error("Error cargando datos iniciales:", error);
    }
  };

  const getAlumnos = async () => {
    if (!filters.tipociclo) {
      notify("warning", "Selecciona un ciclo antes de buscar.");
      return;
    }
    setLoadingBuscar(true);
    try {
      const dto = { ciclo: filters.tipociclo, salon: filters.salon, nombres: searchQuery };
      const response = await alumnoService.getAlumnosByCiclo(dto);
      const data = response?.data || [];
      setListAlumnos(data);
      setListFiltered(data);
      setCurrentPage(1);
      if (data.length === 0) notify("warning", "No se encontraron alumnos con los filtros seleccionados.");
      else notify("success", `${data.length} alumno(s) encontrado(s).`);
    } catch (error) {
      console.error("Error cargando alumnos:", error);
      notify("danger", "Error al obtener los alumnos. Intenta nuevamente.");
    } finally {
      setLoadingBuscar(false);
    }
  };

  const handleSearch = (e) => {
    const query = e.target.value.toLowerCase();
    setSearchQuery(query);
    const filtered = listAlumnos.filter(item => {
      const info = item.alumnoInfo || {};
      return (
        (info.documentoidentidad || "").toLowerCase().includes(query) ||
        (info.nombres || "").toLowerCase().includes(query) ||
        (info.apellidopaterno || "").toLowerCase().includes(query) ||
        (info.apellidomaterno || "").toLowerCase().includes(query)
      );
    });
    setListFiltered(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    if (name === "tipociclo") localStorage.setItem("_c", value);
    if (name === "salon") localStorage.setItem("_s", value);
  };

  const handlePrintCarnet = async (row) => {
    const datos = [
      row.alumnoInfo?.documentoidentidad,
      (row.alumnoInfo?.nombres || "").toUpperCase(),
      (row.alumnoInfo?.apellidopaterno || "").toUpperCase(),
      (row.alumnoInfo?.apellidomaterno || "").toUpperCase(),
      (row.cicloInfo?.denominacion || "").toUpperCase(),
      (row.salonInfo?.denominacion || "").toUpperCase(),
      "CENTRAL",
      row.alumnoInfo?.urlimagen || "",
    ].join("|");

    const url = `${process.env.REACT_APP_ROUTE_API}/reporte/carnet/${encodeURIComponent(datos)}`;

    try {
      await alumnoService.updateCarnetImpreso(row._id);
    } catch (error) {
      console.error("Error actualizando carnet en BD:", error);
    }

    setPrintedMap(prev => ({ ...prev, [row._id]: true }));
    window.open(url, "_blank");
    notify("success", "Carnet generado correctamente.");
  };

  return (
    <>
      <NotificationAlert ref={notificationAlertRef} />
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              {/* Zona 1: Header */}
              <CardHeader>
                <Row className="align-items-center">
                  <Col md="8">
                    <CardTitle tag="h4">Administración de Matrículas</CardTitle>
                  </Col>
                  <Col md="4" className="text-right">
                    <Button
                      color="link"
                      className="btn-icon btn-round"
                      title="Configuración"
                    >
                      <i className="tim-icons icon-settings-gear-63" />
                    </Button>
                  </Col>
                </Row>
              </CardHeader>

              <CardBody>
                {/* Zona 2: Barra de filtros */}
                <Row className="align-items-end mb-4">
                  <Col md="5">
                    <FormGroup className="mb-0">
                      <Label for="search">Búsqueda por DNI, Nombres o Apellidos</Label>
                      <Input
                        type="text"
                        id="search"
                        placeholder="Ingrese términos de búsqueda..."
                        value={searchQuery}
                        onChange={handleSearch}
                        style={{ height: "40px" }}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label for="salon">Salón</Label>
                      <FSelect
                        name="salon"
                        id="salon"
                        value={filters.salon}
                        onChange={handleFilterChange}
                        options={[
                          { value: "0", label: "TODOS LOS SALONES" },
                          ...listSalones.map((s) => ({ value: s._id, label: s.denominacion })),
                        ]}
                      />
                    </FormGroup>
                  </Col>
                  <Col md="3">
                    <FormGroup className="mb-0">
                      <Label for="tipociclo">Ciclo Académico</Label>
                      <FSelect
                        name="tipociclo"
                        id="tipociclo"
                        value={filters.tipociclo}
                        onChange={handleFilterChange}
                        options={listCiclos.map((c) => ({ value: c._id, label: c.denominacion }))}
                        placeholder="Seleccione Ciclo"
                      />
                    </FormGroup>
                  </Col>
                  <Col md="1">
                    <FormGroup className="mb-0">
                      <Button
                        color="info"
                        block
                        onClick={getAlumnos}
                        disabled={loadingBuscar}
                        style={{ height: "40px", padding: "0" }}
                        title="Buscar"
                      >
                        {loadingBuscar ? (
                          <Spinner size="sm" />
                        ) : (
                          <i className="tim-icons icon-zoom-split" />
                        )}
                      </Button>
                    </FormGroup>
                  </Col>
                </Row>

                {/* Zona 3: Barra de acciones */}
                <Row className="mb-4">
                  <Col md="6">
                    {/* Espacio reservado para acciones masivas */}
                  </Col>
                  <Col md="6" className="text-right">
                    <Button
                      color="primary"
                      onClick={() => navigate("/admin/alumno/nuevo")}
                      className="btn-fill"
                    >
                      <i className="tim-icons icon-simple-add mr-2" />
                      Agregar Matrícula
                    </Button>
                  </Col>
                </Row>

                <hr className="my-4" />

                {/* La tabla y paginación se mantienen debajo siguiendo el flujo natural, 
                    pero la estructura de control ha sido rediseñada según lo solicitado */}
                <Table>
                  <thead className="text-primary">
                    <tr>
                      <th>DNI</th>
                      <th>Apellidos y Nombres</th>
                      <th>Salón</th>
                      <th>Fechas</th>
                      <th>Estado</th>
                      <th className="text-right">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {currentItems.map((row, index) => (
                      <tr key={row._id || index}>
                        <td>{row.alumnoInfo?.documentoidentidad}</td>
                        <td>
                          {`${row.alumnoInfo?.apellidopaterno} ${row.alumnoInfo?.apellidomaterno}, ${row.alumnoInfo?.nombres}`}
                        </td>
                        <td>{row.salonInfo?.denominacion || "-"}</td>
                        <td>
                          {fmtDate(row.fechadesde)} —
                          {fmtDate(row.fechahasta)}
                        </td>
                        <td>
                          {row.estado === "1" ? (
                            <span className="text-success">Activo</span>
                          ) : (
                            <span className="text-danger">Inactivo</span>
                          )}
                        </td>
                        <td className="text-right">
                          <Button
                            color="info"
                            size="sm"
                            className="btn-icon btn-link"
                            title="Editar"
                            onClick={() => handleEdit(row)}
                          >
                            <i className="tim-icons icon-pencil" />
                          </Button>
                          {row.alumnoInfo?.urlimagen && (
                            <Button
                              color={(row.carnetimpreso || printedMap[row._id]) ? "success" : "warning"}
                              size="sm"
                              className="btn-icon btn-link"
                              title={(row.carnetimpreso || printedMap[row._id]) ? "Carnet ya impreso" : "Imprimir Carnet"}
                              onClick={() => handlePrintCarnet(row)}
                              disabled={loadingCarnet === row._id}
                            >
                              {loadingCarnet === row._id
                                ? <Spinner size="sm" />
                                : <i className="tim-icons icon-delivery-fast" />}
                            </Button>
                          )}
                        </td>
                      </tr>
                    ))}
                    {listFiltered.length === 0 && (
                      <tr>
                        <td colSpan="6" className="text-center">No se encontraron registros</td>
                      </tr>
                    )}
                  </tbody>
                </Table>
                <CustomPagination
                  itemsPerPage={itemsPerPage}
                  totalItems={listFiltered.length}
                  paginate={paginate}
                  currentPage={currentPage}
                />
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>

    </>
  );
}

export default Alumno;
